import { NavLink } from "react-router"

export interface StatusTab<T extends string = string> {
  key: T
  label: string
  count?: number
  to?: string
  end?: boolean
}

interface StatusTabsProps<T extends string> {
  tabs: readonly StatusTab<T>[]
  activeKey?: T
  onChange?: (key: T) => void
  ariaLabel?: string
}

const tabClass = (isActive: boolean) =>
  `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-forest-950 text-white" : "text-ink/60 hover:bg-gold-500/20 hover:text-ink"
  }`

function TabLabel({ tab }: { tab: StatusTab }) {
  return (
    <>
      {tab.label}
      {tab.count !== undefined && <span className="ml-1.5 text-xs opacity-70">({tab.count})</span>}
    </>
  )
}

export function StatusTabs<T extends string>({ tabs, activeKey, onChange, ariaLabel = "Sections" }: StatusTabsProps<T>) {
  return (
    <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        tab.to ? (
          <NavLink
            key={tab.key}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => tabClass(isActive)}
          >
            <TabLabel tab={tab} />
          </NavLink>
        ) : (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeKey === tab.key}
            onClick={() => onChange?.(tab.key)}
            className={tabClass(activeKey === tab.key)}
          >
            <TabLabel tab={tab} />
          </button>
        )
      ))}
    </div>
  )
}
